import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { PropsWithChildren, ReactElement } from 'react';
import { apiClient, connectClient } from '@services/api/client';
import { env } from '@config/env';
import { logger } from '@services/logger/logger';
import type { Permission } from './permissions';
import type { AuthContextValue, User } from './types';
import * as connectAuth from './connectAuth';

/**
 * Auth backed by the Verofy Connect Node backend (`verofy-connect-api`).
 * The two-step UI (credentials → MFA) maps onto connect's single login call:
 * `POST /api/auth/login` returns a Bearer token straight away unless the account
 * has MFA enabled, in which case it 402s for an MFA code that is re-submitted on
 * the same endpoint. The public interface (AuthContextValue) is unchanged.
 *
 * ponytail: session (token + profile) cached in sessionStorage so a reload
 * restores it. Upgrade path (httpOnly refresh cookie + GET /api/user/me on boot)
 * when the backend supports it.
 */
const SESSION_KEY = 'pfl.session';

interface StoredSession {
  readonly token: string;
  readonly user: User;
}

function readSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

function writeSession(session: StoredSession): void {
  try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(session)); } catch { /* storage unavailable */ }
}

function clearSession(): void {
  try { sessionStorage.removeItem(SESSION_KEY); } catch { /* storage unavailable */ }
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren): ReactElement {
  const restored = readSession();
  const [user, setUser] = useState<User | null>(restored?.user ?? null);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [mfaSetupPending, setMfaSetupPending] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Tokens live in a ref — never in state — so a re-render never leaks one into
  // React DevTools' state tree and we don't accidentally serialize them.
  const tokenRef = useRef<string | null>(restored?.token ?? null);
  // Credentials captured at step 1, replayed with the MFA code at step 2. Held
  // only for the brief MFA window, then cleared.
  const pendingEmailRef = useRef<string | null>(null);
  const pendingPasswordRef = useRef<string | null>(null);
  // Access token handed back by the first login of an un-enrolled account; it
  // authorises the /mfa/generate + /mfa/verify enrolment calls, then is dropped.
  const mfaSetupTokenRef = useRef<string | null>(null);

  useEffect(() => {
    // Both clients now go through the Connect backend — the Management client
    // directly, and the main-api (AI /v2) client through Connect's proxy — so the
    // session token is the single credential and a 401 on either genuinely means
    // the session is dead. One adapter for both.
    const adapter = {
      getToken: () => tokenRef.current ?? env.VITE_DEV_BEARER ?? null,
      refresh: async () => {
        // No server refresh endpoint is wired yet — return the current token so a
        // transient 401 retries once; a genuine 401 falls through to onUnauthorized.
        return tokenRef.current;
      },
      onUnauthorized: () => {
        logger.warn('auth.unauthorized — clearing session');
        tokenRef.current = null;
        clearSession();
        setUser(null);
      },
    };
    apiClient.setAuthAdapter(adapter);
    connectClient.setAuthAdapter(adapter);
  }, []);

  const establishSession = useCallback((result: connectAuth.LoginResult): void => {
    tokenRef.current = result.accessToken;
    writeSession({ token: result.accessToken, user: result.user });
    pendingEmailRef.current = null;
    pendingPasswordRef.current = null;
    mfaSetupTokenRef.current = null;
    setPendingEmail(null);
    setMfaSetupPending(false);
    setUser(result.user);
  }, []);

  // Step 1 — email + password. Connect returns a token immediately unless MFA is
  // enabled, in which case we advance to the MFA step. (The isAuthenticated guard
  // redirects past the MFA screen when no code is needed.)
  const signIn = useCallback(async (email: string, password: string): Promise<string | null> => {
    setIsLoading(true);
    try {
      const trimmed = email.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return 'Enter a valid work email address.';
      if (password === '') return 'Enter your password.';

      const outcome = await connectAuth.login(trimmed, password);
      if (outcome.kind === 'ok') {
        establishSession(outcome.result);
        return null;
      }
      if (outcome.kind === 'mfa-setup') {
        // Un-enrolled account: hold the token for the QR/verify calls, show enrol screen.
        mfaSetupTokenRef.current = outcome.accessToken;
        setMfaSetupPending(true);
        return null;
      }
      if (outcome.kind === 'mfa-required') {
        pendingEmailRef.current = trimmed;
        pendingPasswordRef.current = password;
        setPendingEmail(trimmed);
        return null;
      }
      return outcome.message;
    } finally {
      setIsLoading(false);
    }
  }, [establishSession]);

  // Step 2 — replay the credentials with the MFA code on the same login endpoint.
  const verifyMfa = useCallback(async (code: string): Promise<string | null> => {
    setIsLoading(true);
    try {
      const email = pendingEmailRef.current;
      const password = pendingPasswordRef.current;
      if (email === null || password === null) return 'Your sign-in session expired. Please start again.';
      if (!/^\d{6}$/.test(code)) return 'Enter the full 6-digit code.';

      const outcome = await connectAuth.login(email, password, code);
      if (outcome.kind === 'ok') {
        establishSession(outcome.result);
        return null;
      }
      if (outcome.kind === 'error') return outcome.message;
      // 'mfa-required' (wrong code) or an unexpected 'mfa-setup' — surface as a retry.
      return 'That code is incorrect. Please try again.';
    } finally {
      setIsLoading(false);
    }
  }, [establishSession]);

  const cancelSignIn = useCallback((): void => {
    pendingEmailRef.current = null;
    pendingPasswordRef.current = null;
    mfaSetupTokenRef.current = null;
    setPendingEmail(null);
    setMfaSetupPending(false);
  }, []);

  // MFA enrolment step 1 — fetch the TOTP QR using the token from the initial login.
  const generateMfaQr = useCallback(async (): Promise<string | null> => {
    const token = mfaSetupTokenRef.current;
    if (token === null) return null;
    return connectAuth.generateMfa(token);
  }, []);

  // MFA enrolment step 2 — confirm the code. On success connect drops the token and the
  // operator signs in again (now via the MFA-input step); we clear the setup state to match.
  const confirmMfaSetup = useCallback(async (code: string): Promise<string | null> => {
    const token = mfaSetupTokenRef.current;
    if (token === null) return 'Your enrolment session expired. Please sign in again.';
    if (!/^\d{6}$/.test(code)) return 'Enter the full 6-digit code.';
    const res = await connectAuth.verifyMfaSetup(token, code);
    if (!res.ok) return res.message;
    mfaSetupTokenRef.current = null;
    setMfaSetupPending(false);
    return null;
  }, []);

  // Mirrors connect's handleForgotPassword — POST the email to the reset endpoint.
  const forgotPassword = useCallback(async (email: string): Promise<string | null> => {
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return 'Enter a valid work email address.';
    const res = await connectAuth.forgotPassword(trimmed);
    return res.ok ? null : res.message;
  }, []);

  // Programmatic sign-in is no longer a mock shortcut — there is no session to
  // grant without real credentials. Kept on the interface for compatibility.
  const login = useCallback(async (): Promise<void> => {
    logger.warn('auth.login() shortcut is a no-op under real auth — use signIn()/verifyMfa()');
    await Promise.resolve();
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      await connectAuth.logoutRequest(tokenRef.current);
      tokenRef.current = null;
      pendingEmailRef.current = null;
      pendingPasswordRef.current = null;
      setPendingEmail(null);
      clearSession();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const hasPermission = useCallback(
    (p: Permission): boolean => user?.permissions.includes(p) ?? false,
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      pendingEmail,
      mfaSetupPending,
      login,
      logout,
      signIn,
      verifyMfa,
      cancelSignIn,
      forgotPassword,
      generateMfaQr,
      confirmMfaSetup,
      hasPermission,
    }),
    [user, isLoading, pendingEmail, mfaSetupPending, login, logout, signIn, verifyMfa, cancelSignIn, forgotPassword, generateMfaQr, confirmMfaSetup, hasPermission],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
